// A small path router for the six console destinations. apps/web/server.ts
// already falls back to index.html for any unknown path, so real paths
// ("/insight") work on reload and can be shared as links, which hash routes
// would not survive alongside the shell's "#main" skip link.
import { useEffect, useState } from "react";
import { stripTenant } from "./tenant";

export type RouteId = "dashboard" | "insight" | "action" | "benchmark" | "data" | "ai";

export const ROUTES: Record<RouteId, string> = {
  dashboard: "/",
  insight: "/insight",
  action: "/action",
  benchmark: "/benchmark",
  data: "/data",
  ai: "/ai",
};

/** The route for a path, with or without an industry prefix ("/pmb/insight"). */
export function routeFromPath(pathname: string): RouteId {
  const clean = stripTenant(pathname).replace(/\/+$/, "") || "/";
  const hit = (Object.keys(ROUTES) as RouteId[]).find((id) => ROUTES[id] === clean);
  return hit ?? "dashboard";
}

const NAV_EVENT = "con:navigate";

export function navigate(path: string): void {
  if (typeof history === "undefined" || location.pathname === path) return;
  history.pushState(null, "", path);
  dispatchEvent(new Event(NAV_EVENT));
}

/** The current route, kept in sync with back, forward, and in-app links. */
export function useRoute(): RouteId {
  return routeFromPath(usePathname());
}

/**
 * The current pathname, kept in sync with back, forward, and in-app links.
 * The path carries both the industry and the page, so an industry switch on
 * the same page still re-renders.
 */
export function usePathname(): string {
  const read = () => (typeof location === "undefined" ? "/" : location.pathname);
  const [pathname, setPathname] = useState<string>(read);

  useEffect(() => {
    const sync = () => setPathname(read());
    // Same-origin links to a known route are handled here, so the shell's
    // plain <a href> stays a real link (middle click, copy link, no JS) and
    // only an ordinary left click is turned into a client-side navigation.
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.target || anchor.hasAttribute("download")) return;
      const url = new URL(anchor.href, location.href);
      if (url.origin !== location.origin || url.hash) return;
      const known = Object.values(ROUTES).includes(stripTenant(url.pathname).replace(/\/+$/, "") || "/");
      if (!known) return;
      event.preventDefault();
      navigate(url.pathname);
    };
    addEventListener("popstate", sync);
    addEventListener(NAV_EVENT, sync);
    document.addEventListener("click", onClick);
    return () => {
      removeEventListener("popstate", sync);
      removeEventListener(NAV_EVENT, sync);
      document.removeEventListener("click", onClick);
    };
  }, []);

  return pathname;
}
